// src/pages/dms/OMS.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NamaFormConfig from "../../components/dms/oms/nama-form-config/NamaFormConfig";
import KategoriFormConfig from "../../components/dms/oms/kategori-form-config/KategoriFormConfig";
import SubKategoriFormConfig from "../../components/dms/oms/sub-kategori-form-config/SubKategoriFormConfig";
import FieldItemFormConfig from "../../components/dms/oms/field-item-form-config/FieldItemFormConfig";
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";

// 

// Definisikan data tab untuk OMS
const tabConfigurations = [
    {
        label: "Nama Form",
        component: NamaFormConfig,
        dbPath: "db_config/db_observation/tb_nama_form",
        fieldName: "nama_form",
        primaryKeyField: "id_form",
    },
    {
        label: "Kategori Form",
        component: KategoriFormConfig,
        dbPath: "db_config/db_observation/tb_kategori_form",
        fieldName: "nama_kategori_form",
        primaryKeyField: "id_kategori_form",
    },
    {
        label: "Sub Kategori Form",
        component: SubKategoriFormConfig,
        dbPath: "db_config/db_observation/tb_sub_kategori_form",
        fieldName: "nama_sub_kategori_form",
        primaryKeyField: "id_sub_kategori_form",
    },
    {
        label: "Field Item Form",
        component: FieldItemFormConfig,
        dbPath: "db_config/db_observation/tb_field_item_form",
        fieldName: "nama_field",
        primaryKeyField: "id_field",
    },
];

function OMS() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();
    
    const [tabIndex, setTabIndex] = useState(() => {
        const storedTabIndex = localStorage.getItem('omsTabIndex');
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        return 0;
    });

    useEffect(() => {
        localStorage.setItem('omsTabIndex', tabIndex.toString());
    }, [tabIndex]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const currentTabConfig = tabConfigurations[tabIndex];

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs dan Judul Halaman */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box>
                    <Breadcrumbs aria-label="breadcrumb">
                        {breadcrumbs && breadcrumbs.map((crumb, index) => (
                            crumb.path ? (
                                <MuiLink
                                    key={index}
                                    underline="hover"
                                    color="inherit"
                                    onClick={() => navigate(crumb.path)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {crumb.label}
                                </MuiLink>
                            ) : (
                                <Typography key={index} color="text.primary">
                                    {crumb.label}
                                </Typography>
                            )
                        ))}
                    </Breadcrumbs>
                </Box>
            </Box>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Konten utama */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render komponen secara dinamis berdasarkan tabIndex */}
                            {currentTabConfig && (
                                <currentTabConfig.component
                                    title={`Daftar ${currentTabConfig.label}`}
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Sidebar desktop */}
                {!isMobile && (
                    <Paper elevation={1} sx={{ alignSelf: "flex-start" }}>
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default OMS;